import { useCallback } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  nearbyOtherRidesIdsBatchAtom,
  normalizeRideEntity,
  RidesApi,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";

export const useRideNearbyActions = () => {
  const { entity: entityFromContext } = usePageContext();
  const entity = normalizeRideEntity(entityFromContext);
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));

  const setNearbyOtherRidesIdsBatch = useSetRecoilState(
    nearbyOtherRidesIdsBatchAtom,
  );

  const fetchNearbyRidesBatch = useCallback(
    async (rideIds: number[]) => {
      setNearbyOtherRidesIdsBatch(prev => ({ ...prev, isLoading: true }));

      const result = await RidesApi.searchNearbyRidesBatch(
        rideIds,
        calendarDates,
      );

      if (!result?.success) {
        setNearbyOtherRidesIdsBatch(prev => ({
          ...prev,
          error: result?.error,
          isLoading: false,
        }));
        return;
      }

      const ids = Object.keys(result.data as Record<number, number[]>).map(
        Number,
      );

      setNearbyOtherRidesIdsBatch(prev => ({
        data: {
          ...prev.data,
          ...(result.data as Record<number, number[]>),
        },
        error: undefined,
        isLoading: false,
      }));

      return ids;
    },
    [calendarDates, setNearbyOtherRidesIdsBatch],
  );

  const resetNearbyCache = (): void => {
    setNearbyOtherRidesIdsBatch({
      isLoading: false,
      error: undefined,
      data: {},
    });
  };

  const refetchAllNearbyByRideAndCurrent = useEvent(async (rideId: number) => {
    await fetchNearbyRidesBatch([rideId]);
  });

  return {
    fetchNearbyRidesBatch,
    refetchAllNearbyByRideAndCurrent,
    resetNearbyCache,
  };
};
